import React, { useState, useEffect } from 'react';
import { ExternalLink, MessageSquare, GitBranch, Eye, Maximize2 } from 'lucide-react';
import * as figmaService from '../../services/figmaService';
import { FigmaFileDetails } from '../../services/figmaService';

interface FigmaEmbedViewerProps {
  fileId: number;
  token: string;
  compact?: boolean;
}

const FigmaEmbedViewer: React.FC<FigmaEmbedViewerProps> = ({ fileId, token, compact = false }) => {
  const [fileDetails, setFileDetails] = useState<FigmaFileDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [showVersions, setShowVersions] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<string | null>(null);

  useEffect(() => {
    fetchFileDetails();
  }, [fileId]);

  const fetchFileDetails = async () => {
    try {
      setLoading(true);
      const data = await figmaService.getFigmaFileDetails(fileId, token);
      setFileDetails(data);
    } catch (error) {
      console.error('Failed to fetch Figma file details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="aspect-video bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!fileDetails) {
    return null;
  }

  const unresolvedComments = fileDetails.comments.filter(c => !c.resolved).length;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* File Header */}
      <div className="bg-gray-900 p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xs">F</span>
          </div>
          <div>
            <h4 className="text-sm font-medium text-white">{fileDetails.file_name}</h4>
            <p className="text-xs text-gray-400">{fileDetails.instance_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowVersions(!showVersions)}
            className="p-1.5 text-gray-300 hover:bg-gray-800 rounded transition-colors"
            title="Version history"
          >
            <GitBranch className="w-4 h-4" />
          </button>
          <a
            href={figmaService.getFigmaFileUrl(fileDetails.file_key)}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 text-gray-300 hover:bg-gray-800 rounded transition-colors"
            title="Open in Figma"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Embed Viewer */}
      {!compact && (
        <div className="relative aspect-video bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
          <iframe
            src={fileDetails.embedUrl}
            className="absolute inset-0 w-full h-full"
            allowFullScreen
          />
          {/* Frame Indicator */}
          <div className="absolute bottom-4 left-4 bg-black/75 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <p className="text-xs text-white">Frame 1 / 1</p>
          </div>
        </div>
      )}

      {/* Footer Bar */}
      <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200">
        <div className="flex items-center gap-4">
          <div className="text-xs text-gray-600">
            <span className="font-medium text-gray-900">{unresolvedComments}</span> unresolved comments
          </div>
          {fileDetails.versions.length > 0 && (
            <div className="text-xs text-gray-600">
              <span className="font-medium text-gray-900">{fileDetails.versions.length}</span> versions
            </div>
          )}
        </div>
        <a
          href={figmaService.getFigmaFileUrl(fileDetails.file_key)}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          View in Figma
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Version History */}
      {showVersions && fileDetails.versions.length > 0 && (
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-3">
            <GitBranch className="w-3.5 h-3.5" />
            Version History
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {fileDetails.versions.slice(0, 10).map((version) => (
              <div
                key={version.id}
                className="flex gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
                onClick={() => setSelectedVersion(version.version_id)}
              >
                <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                  version.is_current ? 'bg-blue-600' : 'bg-gray-300'
                }`}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium text-gray-900">
                      {version.label || `Version ${version.version_id.substring(0, 8)}`}
                    </span>
                    {version.is_current && (
                      <span className="text-xs text-gray-500">(current)</span>
                    )}
                  </div>
                  {version.description && (
                    <p className="text-xs text-gray-600 line-clamp-2">{version.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(version.created_at).toLocaleDateString()} · {version.created_by || 'Unknown'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments Section */}
      {fileDetails.comments.length > 0 && !compact && (
        <div className="border-t border-gray-200 p-4">
          <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Comments ({fileDetails.comments.length})
          </h5>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {fileDetails.comments.slice(0, 5).map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-medium">
                    {comment.user_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-900">{comment.user_name}</span>
                    {comment.node_id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 12.483V1.005A1.001 1.001 0 0 0 23.013 0z"/>
                        </svg>
                        Figma
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{comment.message}</p>
                  {comment.resolved && (
                    <span className="text-xs text-green-600 font-medium">✓ Resolved</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FigmaEmbedViewer;
